import { Button, Center, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useGetAuthenticatedPortalSourcingEventQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { setOrganizationId } from "../../../common/local-storage/selected-organization";
import { urls } from "../../../urls";
import {
  organizationsThatCanOpen,
  probeBuyerEventAccess,
  type EventAccessProbe,
  type OrganizationRef,
} from "./organizationsThatCanOpen";

export const EventUnavailable = ({
  eventId,
  probe = probeBuyerEventAccess,
}: {
  eventId: string;
  probe?: EventAccessProbe;
}) => {
  const { t } = useTranslation();
  const { organizations, selectedOrg } = useLoggedInWithOrgContextState();
  const asSupplier = useGetAuthenticatedPortalSourcingEventQuery({ eventId });
  const notASupplierEither = !asSupplier.isLoading && !asSupplier.data;
  const [openable, setOpenable] = useState<OrganizationRef[] | null>(null);

  useEffect(() => {
    if (!notASupplierEither) return;
    let cancelled = false;
    setOpenable(null);
    const others = organizations.filter((org) => org.id !== selectedOrg.id);
    void organizationsThatCanOpen(eventId, others, probe).then((found) => {
      if (cancelled) return;
      if (found.length === 1) openAs(found[0].id);
      else setOpenable(found);
    });
    return () => {
      cancelled = true;
    };
  }, [eventId, notASupplierEither, organizations, selectedOrg.id, probe]);

  if (asSupplier.data) return <Navigate to={urls.v2.supplier.procurement.go(eventId)} replace />;

  if (openable == null) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  return (
    <Center py="20">
      <VStack spacing="4">
        <Text color="smError">{t("Could not load this sourcing event.")}</Text>
        {openable.length > 0 && (
          <>
            <Text color="smMuted">{t("It belongs to another of your organizations. Open it as:")}</Text>
            <HStack>
              {openable.map((org) => (
                <Button key={org.id} variant="outline" onClick={() => openAs(org.id)}>
                  {org.name}
                </Button>
              ))}
            </HStack>
          </>
        )}
      </VStack>
    </Center>
  );
};

const openAs = (organizationId: string) => {
  setOrganizationId(organizationId);
  window.location.reload();
};
